<script setup lang="ts">
import { computed } from 'vue';
import ConnxStatusRowCard from '@/components/Atoms/ConnxStatusRowCard.vue';
import type { Status, StatusDangerLevel } from '@/interfaces/Dashboard';
import ConnxProgressBar from '@/components/Atoms/ConnxProgressBar.vue';

// ───────────────────────────── Props ─────────────────────────────
const props = defineProps<{
  title: string;
  subtitle?: string;
  level?: StatusDangerLevel;
  score: number | string;
  progress: number;
  stats?: { name: string; value: number | string }[]; // dynamic stats array
  showList?: boolean;
  valueSuffix?: string;
  series?: { name: string; data: number; status?: Status }[];
}>();

// ───────────────────────────── Level-color map ─────────────────────────────
const levelColorMap: Record<
  StatusDangerLevel,
  { bg: string; progress: string; border: string; status: Status; text: string }
> = {
  high: {
    bg: 'tw-bg-connx-danger-tint-95',
    progress: 'tw-bg-connx-danger',
    border: 'tw-border-connx-danger',
    status: 'danger',
    text: 'tw-text-connx-danger',
  },
  medium: {
    bg: 'tw-bg-connx-warning-tint-95',
    progress: 'tw-bg-connx-warning',
    border: 'tw-border-connx-warning',
    status: 'warning',
    text: 'tw-text-connx-warning',
  },
  low: {
    bg: 'tw-bg-connx-success-tint-95',
    progress: 'tw-bg-connx-success',
    border: 'tw-border-connx-success',
    status: 'success',
    text: 'tw-text-connx-success',
  },
};

const currentColors = computed(() => {
  const key = props.level as StatusDangerLevel | undefined;
  return levelColorMap[key ?? 'low'];
});

// ───────────────────────────── Stats list ─────────────────────────────
const statsSeries = computed(() => {
  if (props.stats?.length) {
    return props.stats.map((s) => ({
      name: s.name,
      data: s.value,
      status: currentColors.value.status,
    }));
  }
  return [];
});
</script>
<template>
  <div
    class="tw-grid tw-grid-rows-[auto_auto] tw-rounded-lg tw-p-4 tw-w-full tw-text-sm tw-border-2 tw-border-solid"
    :class="[currentColors.bg, currentColors.border]"
  >
    <!-- Top row: Title -->
    <div
      class="tw-text-lg md:tw-text-2xl lg:tw-text-2xl tw-text-connx-grey-shade-55 tw-break-words tw-whitespace-normal tw-leading-snug tw-text-left"
    >
      {{ title }}
    </div>

    <!-- Bottom row: Level | Score | Stats -->
    <div class="tw-flex tw-w-full tw-items-end tw-gap-6">
      <!-- Level + Score -->
      <div class="tw-flex tw-items-end tw-gap-6 tw-flex-1">
        <!-- Level (wider) -->
        <span
          class="tw-text-connx-white tw-text-lg md:tw-text-xl tw-font-bold tw-px-14 md:tw-px-16 tw-py-1.5 tw-rounded-full tw-text-center tw-shrink-0"
          :style="{ minWidth: '120px' }"
          :class="currentColors.progress"
        >
          {{ level ? level.charAt(0).toUpperCase() + level.slice(1).toLowerCase() : '' }}
        </span>

        <!-- Score -->
        <div class="tw-flex-1 tw-flex tw-items-end tw-justify-center">
          <span class="tw-text-4xl tw-font-semibold tw-leading-none" :class="currentColors.text">
            {{ score }}
          </span>
        </div>
      </div>

      <!-- Stats column (DO NOT CHANGE) -->
      <div class="tw-flex tw-flex-col tw-gap-1 tw-justify-center tw-w-[160px]">
        <ConnxStatusRowCard
          v-for="(item, index) in statsSeries"
          :key="index"
          :status="item.status"
          :title="item.name"
          :value="`${item.data}${valueSuffix ?? ''}`"
        />
      </div>
    </div>

    <!-- Progress bar -->
    <div class="tw-mt-4 tw-px-1 tw-pb-1">
      <connx-progress-bar
        :percentage="progress"
        :background-color="currentColors.progress"
        background-fill="tw-bg-connx-grey-tint-70"
      >
      </connx-progress-bar>
    </div>
  </div>
</template>
